/**
 * iOS Safari routes Web Audio API output to the "Ambient" audio session
 * category by default, which is silenced whenever the device's hardware
 * ring/silent switch is set to silent (or Silent Mode is enabled in Control
 * Center). HTMLAudioElement playback uses the "Playback" category, which is
 * not affected by the silent switch.
 *
 * Playing a silent looping <audio> element from within a user gesture
 * promotes the page's audio session to "Playback" for the lifetime of that
 * playback. While the silent loop is alive, subsequent Web Audio API output
 * is also routed through "Playback" and ignores the silent switch.
 *
 * This module owns a single, lazily-created silent <audio> element and
 * exposes start/stop helpers. `unlockAudioSession()` MUST be called
 * synchronously inside a user gesture handler (e.g. an onClick) - calling it
 * after an `await` boundary will be rejected by Safari's autoplay policy.
 */

let silentAudio: HTMLAudioElement | null = null
let silentAudioUrl: string | null = null
let active = false

function buildSilentWavUrl(): string {
  const sampleRate = 8000
  const durationSeconds = 1
  const numSamples = sampleRate * durationSeconds
  const buffer = new ArrayBuffer(44 + numSamples * 2)
  const view = new DataView(buffer)

  const writeAscii = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i))
    }
  }

  writeAscii(0, "RIFF")
  view.setUint32(4, 36 + numSamples * 2, true)
  writeAscii(8, "WAVE")
  writeAscii(12, "fmt ")
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, 1, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * 2, true)
  view.setUint16(32, 2, true)
  view.setUint16(34, 16, true)
  writeAscii(36, "data")
  view.setUint32(40, numSamples * 2, true)

  const blob = new Blob([buffer], { type: "audio/wav" })
  return URL.createObjectURL(blob)
}

function getSilentAudio(): HTMLAudioElement | null {
  if (typeof window === "undefined") return null
  if (silentAudio) return silentAudio
  silentAudioUrl = buildSilentWavUrl()
  const audio = new Audio(silentAudioUrl)
  audio.loop = true
  audio.preload = "auto"
  audio.setAttribute("playsinline", "")
  silentAudio = audio
  return audio
}

export function unlockAudioSession(): void {
  const audio = getSilentAudio()
  if (!audio) return
  active = true
  if (audio.paused) {
    void audio.play().catch(() => {
      active = false
    })
  }
}

export function releaseAudioSession(): void {
  if (!active) return
  active = false
  if (silentAudio) {
    silentAudio.pause()
    try {
      silentAudio.currentTime = 0
    } catch {
      // ignore - some browsers reject seeks on paused media
    }
  }
}

export function isAudioSessionActive(): boolean {
  return active
}

export function _resetAudioSessionForTests(): void {
  active = false
  silentAudio = null
  if (silentAudioUrl) {
    try {
      URL.revokeObjectURL(silentAudioUrl)
    } catch {
      // ignore
    }
    silentAudioUrl = null
  }
}
